import { useCallback, useEffect, useRef, useState } from "react";

const platform = () => {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1))
    return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

// Steps for re-allowing location once the browser stopped asking. Browsers
// never show the permission prompt again after a "Block", so the applicant
// has to flip it back in site/device settings.
const HELP_STEPS = {
  ios: [
    "Open iPhone Settings → Privacy & Security → Location Services and turn it on.",
    "In the same list, open Safari Websites (or Chrome) and choose “While Using the App”.",
    "Back in the browser, tap “aA” (Safari) or the lock icon → Website Settings → Location → Allow.",
    "Return here and tap Enable location.",
  ],
  android: [
    "Turn on Location from the quick settings panel (swipe down from the top).",
    "Tap the lock / settings icon next to the address bar → Permissions → Location → Allow.",
    "Return here and tap Enable location.",
  ],
  desktop: [
    "Click the icon at the left of the address bar (lock / sliders).",
    "Set Location to Allow (or “Reset permission”).",
    "Make sure location services are on for your browser in system settings.",
    "Click Enable location again.",
  ],
};

const REASONS = {
  checking:
    "Waiting for location access. Allow the browser location request to continue.",
  dismissed:
    "Location permission was not granted. Tap Enable location and choose Allow.",
  denied:
    "Location access is blocked for this site. Follow the steps below to allow it, then tap Enable location.",
  unavailable:
    "We couldn't get your location. Turn on device location (GPS), then tap Enable location.",
  insecure:
    "Location only works on a secure (https) connection. Open this page over https to continue.",
};

// Gates a step on the browser's geolocation. The "Enable location" button asks
// again from the click itself (browsers re-show the prompt if it was only
// dismissed), and the gate re-checks when the site permission changes or the
// tab regains focus, so an applicant who allows location later does not have
// to reload the page.
export default function LocationGate({ children, enabled = true }) {
  const [status, setStatus] = useState("checking");
  const [hasLocation, setHasLocation] = useState(false);
  const requestId = useRef(0);

  const request = useCallback(() => {
    const id = ++requestId.current;
    if (window.isSecureContext === false) {
      setStatus("insecure");
      return;
    }
    if (!navigator.geolocation) {
      setStatus("unavailable");
      return;
    }
    setStatus("checking");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (id !== requestId.current) return;
        setHasLocation(true);
        setStatus("ready");
        fetch(`${process.env.REACT_APP_API_URL || ""}/api/auth/location`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            latitude: coords.latitude,
            longitude: coords.longitude,
          }),
        }).catch(() => {});
      },
      (error) => {
        if (id !== requestId.current) return;
        if (error.code !== 1) {
          setStatus("unavailable");
          return;
        }
        // PERMISSION_DENIED covers both "Block" and a closed prompt; only a
        // closed prompt can be asked again from the button.
        if (!navigator.permissions?.query) {
          setStatus("denied");
          return;
        }
        navigator.permissions
          .query({ name: "geolocation" })
          .then((result) => {
            if (id === requestId.current)
              setStatus(result.state === "prompt" ? "dismissed" : "denied");
          })
          .catch(() => {
            if (id === requestId.current) setStatus("denied");
          });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, []);

  useEffect(() => {
    if (!enabled || hasLocation) return undefined;
    request();
    return () => {
      requestId.current += 1;
    };
  }, [enabled, hasLocation, request]);

  // Permission changed in site settings: ask again right away when allowed,
  // and switch the copy to "tap to allow" when it was reset to ask.
  useEffect(() => {
    if (!enabled || hasLocation || !navigator.permissions?.query)
      return undefined;
    let permission = null;
    let cancelled = false;
    const onChange = () => {
      if (permission?.state === "granted") request();
      else if (permission?.state === "prompt") setStatus("dismissed");
    };
    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (cancelled) return;
        permission = result;
        result.addEventListener("change", onChange);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      permission?.removeEventListener("change", onChange);
    };
  }, [enabled, hasLocation, request]);

  // Coming back from device settings or another tab: check again silently.
  // A closed prompt is left for the button so it doesn't pop up on every focus.
  useEffect(() => {
    if (!enabled || hasLocation || status === "checking" || status === "dismissed")
      return undefined;
    const onFocus = () => request();
    const onVisible = () => {
      if (document.visibilityState === "visible") request();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, hasLocation, status, request]);

  const blocked = enabled && !hasLocation;
  const reason = blocked ? REASONS[status] || REASONS.unavailable : "";
  const steps = status === "denied" ? HELP_STEPS[platform()] : null;
  const prompt = blocked ? (
    <div
      role="status"
      style={{
        padding: "12px 16px",
        marginBottom: 16,
        borderRadius: 12,
        background: "#fffbeb",
        border: "1px solid #fde68a",
        color: "#92400e",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <strong>Enable location to continue</strong>
          <div style={{ fontSize: 13, marginTop: 4 }}>{reason}</div>
        </div>
        {status !== "insecure" && (
          <button
            className="alp-btn-primary"
            type="button"
            disabled={status === "checking"}
            onClick={request}
          >
            {status === "checking" ? "Checking location…" : "Enable location"}
          </button>
        )}
      </div>
      {steps && (
        <ol style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
    </div>
  ) : null;

  return children({ blocked, prompt, reason, status, retry: request });
}
